/**
 * Enhanced Gaming PC Selector
 * Handles both legacy (color only) and enhanced (processor + color) modes
 * with smooth animations and improved UX
 */

class GamingEnhancedSelector {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.enhancedMode = container.dataset.enhancedMode === 'true';
    
    // Elements
    this.collections = container.querySelectorAll('.gaming-color-selector__collection');
    this.collectionsContainer = container.querySelector('.gaming-color-selector__collections');
    this.loading = container.querySelector('.gaming-color-selector__loading');
    
    // Initialize based on mode
    if (this.enhancedMode) {
      this.initEnhancedMode();
    } else {
      this.initLegacyMode();
    }
    
    this.preloadImages();
  }
  
  initEnhancedMode() {
    // Enhanced mode: Processor + Color selectors
    this.processorOptions = this.container.querySelectorAll('[data-processor]');
    this.colorOptions = this.container.querySelectorAll('[data-color]');
    
    this.currentProcessor = null;
    this.currentColor = null;
    
    this.bindEnhancedEvents();
    this.setEnhancedInitialState();
  }
  
  initLegacyMode() {
    // Legacy mode: Color only
    this.colorOptions = this.container.querySelectorAll('.gaming-color-selector__option');
    this.currentColor = null;
    
    this.bindLegacyEvents();
    this.setLegacyInitialState();
  }
  
  bindEnhancedEvents() {
    // Processor selector events
    this.processorOptions.forEach(option => {
      option.addEventListener('click', (e) => this.handleProcessorSwitch(e));
      option.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.handleProcessorSwitch(e);
        }
      });
    });
    
    // Color selector events
    this.colorOptions.forEach(option => {
      option.addEventListener('click', (e) => this.handleColorSwitch(e));
      option.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.handleColorSwitch(e);
        }
      });
    });
    
    // Add swipe support for mobile
    if (this.isMobile()) {
      this.addEnhancedSwipeSupport();
    }
  }
  
  bindLegacyEvents() {
    // Color selector events only
    this.colorOptions.forEach(option => {
      option.addEventListener('click', (e) => this.handleLegacyColorSwitch(e));
      option.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.handleLegacyColorSwitch(e);
        }
      });
    });
    
    // Add swipe support for mobile
    if (this.isMobile()) {
      this.addLegacySwipeSupport();
    }
  }
  
  setEnhancedInitialState() {
    // Set initial processor
    const activeProcessor = this.container.querySelector('[data-processor].active');
    if (activeProcessor) {
      this.currentProcessor = activeProcessor.dataset.processor;
    }
    
    // Set initial color
    const activeColor = this.container.querySelector('[data-color].active');
    if (activeColor) {
      this.currentColor = activeColor.dataset.color;
    }
  }
  
  setLegacyInitialState() {
    // Set initial color
    const activeColor = this.container.querySelector('.gaming-color-selector__option.active');
    if (activeColor) {
      this.currentColor = activeColor.dataset.color;
    }
  }
  
  handleProcessorSwitch(event) {
    const option = event.currentTarget;
    const newProcessor = option.dataset.processor;
    
    // Prevent switching to the same processor
    if (newProcessor === this.currentProcessor) return;
    
    this.switchToProcessor(newProcessor);
  }
  
  handleColorSwitch(event) {
    const option = event.currentTarget;
    const newColor = option.dataset.color;
    
    // Prevent switching to the same color
    if (newColor === this.currentColor) return;
    
    this.switchToColor(newColor);
  }
  
  handleLegacyColorSwitch(event) {
    const option = event.currentTarget;
    const newColor = option.dataset.color;
    
    // Prevent switching to the same color
    if (newColor === this.currentColor) return;
    
    this.switchToLegacyColor(newColor);
  }
  
  switchToProcessor(processor) {
    // Update current processor
    this.currentProcessor = processor;
    
    // Perform smooth transition
    this.performEnhancedTransition();
    
    // Update processor active states
    this.updateProcessorActiveStates(processor);
    
    // Announce to screen readers
    this.announceProcessorChange(processor);
    
    // Track analytics
    this.trackProcessorSelection(processor);
  }
  
  switchToColor(color) {
    // Update current color
    this.currentColor = color;
    
    // Perform smooth transition
    this.performEnhancedTransition();
    
    // Update color active states
    this.updateColorActiveStates(color);
    
    // Announce to screen readers
    this.announceColorChange(color);
    
    // Track analytics
    this.trackColorSelection(color);
  }
  
  switchToLegacyColor(color) {
    // Update current color
    this.currentColor = color;
    
    // Perform smooth transition
    this.performLegacyTransition(color);
    
    // Update color active states
    this.updateColorActiveStates(color);
    
    // Announce to screen readers
    this.announceColorChange(color);
    
    // Track analytics
    this.trackColorSelection(color);
  }
  
  performEnhancedTransition() {
    // Step 1: Fix height to prevent jumping
    this.fixContainerHeight();
    
    // Step 2: Fade out current collection immediately
    this.fadeOutCurrentCollection();
    
    // Step 3: Show loading briefly
    setTimeout(() => {
      this.showLoading();
      
      // Step 4: Switch to new collection quickly
      setTimeout(() => {
        const targetKey = `${this.currentProcessor}-${this.currentColor}`;
        this.switchToCollection(targetKey);
        
        // Step 5: Hide loading and restore height
        setTimeout(() => {
          this.hideLoading();
          this.restoreContainerHeight();
        }, 250);
      }, 100);
    }, 50);
  }
  
  performLegacyTransition(color) {
    // Step 1: Fix height to prevent jumping
    this.fixContainerHeight();
    
    // Step 2: Fade out current collection immediately
    this.fadeOutCurrentCollection();
    
    // Step 3: Show loading briefly
    setTimeout(() => {
      this.showLoading();
      
      // Step 4: Switch to new collection quickly
      setTimeout(() => {
        this.switchToCollection(color);
        
        // Step 5: Hide loading and restore height
        setTimeout(() => {
          this.hideLoading();
          this.restoreContainerHeight();
        }, 250);
      }, 100);
    }, 50);
  }
  
  updateProcessorActiveStates(processor) {
    this.processorOptions.forEach(option => {
      const isActive = option.dataset.processor === processor;
      option.classList.toggle('active', isActive);
      option.setAttribute('aria-pressed', isActive);
    });
    
    // Update sliding indicator
    this.updateProcessorIndicator(processor);
  }
  
  updateColorActiveStates(color) {
    this.colorOptions.forEach(option => {
      const isActive = option.dataset.color === color;
      option.classList.toggle('active', isActive);
      option.setAttribute('aria-pressed', isActive);
    });
    
    // Update sliding indicator
    this.updateColorIndicator(color);
  }
  
  updateProcessorIndicator(processor) {
    const indicator = this.container.querySelector('.gaming-color-selector__indicator--processor');
    if (indicator) {
      if (processor === 'amd') {
        indicator.style.transform = 'translateX(100%)';
      } else {
        indicator.style.transform = 'translateX(0%)';
      }
    }
  }
  
  updateColorIndicator(color) {
    const indicator = this.container.querySelector('.gaming-color-selector__indicator');
    if (indicator) {
      if (color === 'black') {
        indicator.style.transform = 'translateX(100%)';
      } else {
        indicator.style.transform = 'translateX(0%)';
      }
    }
  }
  
  switchToCollection(targetKey) {
    // Remove active state from all collections
    this.collections.forEach(collection => {
      collection.classList.remove('active');
      collection.style.transition = 'none'; // Disable transitions for immediate hide
      collection.style.opacity = '0';
      collection.style.visibility = 'hidden';
      collection.style.transform = 'translateY(15px)';
      collection.style.display = 'none';
    });
    
    // Show target collection with smooth fade in
    const targetCollection = this.container.querySelector(`.gaming-color-selector__collection--${targetKey}`);
    if (targetCollection) {
      // Set initial state without transition
      targetCollection.style.transition = 'none';
      targetCollection.style.display = 'block';
      targetCollection.style.visibility = 'visible';
      targetCollection.style.opacity = '0';
      targetCollection.style.transform = 'translateY(15px)';
      targetCollection.classList.add('active');
      
      // Force reflow to ensure initial state is applied
      targetCollection.offsetHeight;
      
      // Now enable transitions and trigger the fade in
      requestAnimationFrame(() => {
        targetCollection.style.transition = 'opacity 0.3s ease-in, transform 0.3s ease-in';
        targetCollection.style.opacity = '1';
        targetCollection.style.transform = 'translateY(0)';
        
        // Animate in the product cards with a subtle stagger
        setTimeout(() => {
          this.animateProductCards(targetCollection);
        }, 100);
      });
    }
  }
  
  fadeOutCurrentCollection() {
    const activeCollection = this.container.querySelector('.gaming-color-selector__collection.active');
    if (activeCollection) {
      // Only fade out if it's not already transitioning
      if (activeCollection.style.opacity !== '0') {
        activeCollection.style.transition = 'opacity 0.2s ease-out, transform 0.2s ease-out';
        activeCollection.style.opacity = '0';
        activeCollection.style.transform = 'translateY(10px)';
      }
    }
  }
  
  animateProductCards(collection) {
    const cards = collection.querySelectorAll('.gaming-grid__item');
    cards.forEach((card, index) => {
      // Start with initial state
      card.style.opacity = '0.7';
      card.style.transform = 'translateY(8px)';
      
      setTimeout(() => {
        card.style.transition = 'opacity 0.2s ease, transform 0.2s ease';
        card.style.opacity = '1';
        card.style.transform = 'translateY(0)';
      }, index * 25); // Gentle stagger effect
    });
  }
  
  showLoading() {
    if (!this.loading) {
      this.createLoadingElement();
    }
    
    if (this.loading) {
      this.matchLoadingHeight();
      this.loading.style.display = 'flex';
      this.loading.style.opacity = '0';
      this.loading.style.transition = 'opacity 0.25s ease-in';
      
      requestAnimationFrame(() => {
        this.loading.style.opacity = '1';
      });
    }
  }
  
  hideLoading() {
    if (this.loading) {
      this.loading.style.transition = 'opacity 0.3s ease-out';
      this.loading.style.opacity = '0';
      
      setTimeout(() => {
        this.loading.style.display = 'none';
        this.loading.style.height = '';
        this.loading.style.minHeight = '';
        this.loading.style.transition = '';
      }, 300);
    }
  }
  
  matchLoadingHeight() {
    if (!this.loading || !this.collectionsContainer) return;
    
    const containerHeight = this.collectionsContainer.offsetHeight;
    this.loading.style.height = `${containerHeight}px`;
    this.loading.style.minHeight = `${containerHeight}px`;
  }
  
  createLoadingElement() {
    if (!this.loading && this.collectionsContainer) {
      const loadingHtml = `
        <div class="gaming-color-selector__loading" style="display: none; opacity: 0;">
          <div class="gaming-color-selector__loading-spinner"></div>
          <div class="gaming-color-selector__loading-text">
            Loading
            <span class="gaming-color-selector__loading-dots">
              <span class="gaming-color-selector__loading-dot"></span>
              <span class="gaming-color-selector__loading-dot"></span>
              <span class="gaming-color-selector__loading-dot"></span>
            </span>
          </div>
        </div>
      `;
      
      this.collectionsContainer.insertAdjacentHTML('afterbegin', loadingHtml);
      this.loading = this.container.querySelector('.gaming-color-selector__loading');
    }
  }
  
  fixContainerHeight() {
    if (this.collectionsContainer) {
      this.collectionsContainer.offsetHeight;
      const currentHeight = this.collectionsContainer.offsetHeight;
      this.collectionsContainer.style.transition = 'none';
      this.collectionsContainer.style.height = `${currentHeight}px`;
      this.collectionsContainer.style.overflow = 'hidden';
      this.collectionsContainer.offsetHeight;
    }
  }
  
  restoreContainerHeight() {
    if (this.collectionsContainer) {
      this.collectionsContainer.style.transition = 'height 0.3s ease';
      this.collectionsContainer.style.height = 'auto';
      this.collectionsContainer.style.overflow = 'visible';
      
      setTimeout(() => {
        this.collectionsContainer.style.transition = '';
      }, 300);
    }
  }
  
  preloadImages() {
    this.collections.forEach(collection => {
      if (!collection.classList.contains('active')) {
        const images = collection.querySelectorAll('img[data-src]');
        images.forEach(img => {
          if (img.dataset.src) {
            const preloadImg = new Image();
            preloadImg.src = img.dataset.src;
          }
        });
      }
    });
  }
  
  addEnhancedSwipeSupport() {
    let startX = 0;
    let startY = 0;
    let endX = 0;
    let endY = 0;

    this.container.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    });

    this.container.addEventListener('touchend', (e) => {
      endX = e.changedTouches[0].clientX;
      endY = e.changedTouches[0].clientY;
      this.handleEnhancedSwipe();
    });

    this.handleEnhancedSwipe = () => {
      const diffX = startX - endX;
      const diffY = startY - endY;
      
      // Only process horizontal swipes that are longer than vertical
      if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
        const colors = ['white', 'black'];
        const currentColorIndex = colors.indexOf(this.currentColor);
        
        if (diffX > 0 && currentColorIndex < colors.length - 1) {
          // Swipe left - next color
          this.switchToColor(colors[currentColorIndex + 1]);
        } else if (diffX < 0 && currentColorIndex > 0) {
          // Swipe right - previous color
          this.switchToColor(colors[currentColorIndex - 1]);
        }
      }
    };
  }
  
  addLegacySwipeSupport() {
    let startX = 0;
    let startY = 0;
    let endX = 0;
    let endY = 0;

    this.container.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    });

    this.container.addEventListener('touchend', (e) => {
      endX = e.changedTouches[0].clientX;
      endY = e.changedTouches[0].clientY;
      this.handleLegacySwipe();
    });

    this.handleLegacySwipe = () => {
      const diffX = startX - endX;
      const diffY = startY - endY;
      
      // Only process horizontal swipes that are longer than vertical
      if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
        const colors = ['white', 'black'];
        const currentIndex = colors.indexOf(this.currentColor);
        
        if (diffX > 0 && currentIndex < colors.length - 1) {
          // Swipe left - next color
          this.switchToLegacyColor(colors[currentIndex + 1]);
        } else if (diffX < 0 && currentIndex > 0) {
          // Swipe right - previous color
          this.switchToLegacyColor(colors[currentIndex - 1]);
        }
      }
    };
  }
  
  announceProcessorChange(processor) {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = `Now showing ${processor.toUpperCase()} gaming PCs`;
    
    document.body.appendChild(announcement);
    
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }
  
  announceColorChange(color) {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = `Now showing ${color} gaming PCs`;
    
    document.body.appendChild(announcement);
    
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }
  
  trackProcessorSelection(processor) {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'processor_selection', {
        'event_category': 'Gaming PC',
        'event_label': processor,
        'section_id': this.sectionId
      });
    }
    
    if (typeof analytics !== 'undefined') {
      analytics.track('Gaming PC Processor Selected', {
        processor: processor,
        section: this.sectionId
      });
    }
  }
  
  trackColorSelection(color) {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'color_selection', {
        'event_category': 'Gaming PC',
        'event_label': color,
        'section_id': this.sectionId
      });
    }
    
    if (typeof analytics !== 'undefined') {
      analytics.track('Gaming PC Color Selected', {
        color: color,
        section: this.sectionId
      });
    }
  }
  
  isMobile() {
    return window.innerWidth <= 768;
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  const selectors = document.querySelectorAll('[id^="gaming-color-selector-"]');
  selectors.forEach(selector => {
    new GamingEnhancedSelector(selector);
  });
});

// Re-initialize on Shopify section reloads (theme editor)
document.addEventListener('shopify:section:load', function(event) {
  if (event.target.querySelector('[id^="gaming-color-selector-"]')) {
    const selector = event.target.querySelector('[id^="gaming-color-selector-"]');
    new GamingEnhancedSelector(selector);
  }
});
